<template>
  <div class="onboarding onboarding-theme">
    <PageContainer>
      <div class="grid">
        <!-- Left: Assistant header + typed step text -->
        <div class="left">
          <div class="top-row">
            <div class="assistant-row">
              <Icon name="sparkle" size="small" class="assistant-icon" />
              <Text variant="label-medium">Tallo Assistant</Text>
            </div>
          </div>

          <!-- Screen readers get each line once it is complete; the character-by-character view is visual only -->
          <p class="sr-only" aria-live="polite">{{ typedLines.join(' ') }}</p>
          <div class="typed" aria-hidden="true">
            <Text v-for="(line, i) in typedLines" :key="i" variant="h3" class="typed-line">{{ line }}</Text>
            <Text v-if="typedText" variant="h3" class="typed-line">{{ typedText }}</Text>

            <Transition name="btn-fade">
              <div v-if="isWelcome && !typing" class="welcome-cta">
                <Button @click="start">Get started</Button>
              </div>
            </Transition>
          </div>
        </div>

        <!-- Right: Step indicator row + Form, consistent slide-in from right -->
        <div class="right">
          <Transition name="fade-right">
            <div v-if="!isWelcome && stepIndex >= 0" v-show="!typing" class="right-step-row">
              <Text variant="label-small" color="secondary">Step {{ stepIndex + 1 }} of {{ totalSteps }}</Text>
              <div class="dot-row">
                <button
                  v-for="(s, i) in stepperSteps"
                  :key="s.name"
                  class="step-dot"
                  :class="{ active: i === stepIndex, done: i < stepIndex, locked: !isStepAllowed(i) }"
                  :aria-current="i === stepIndex ? 'step' : undefined"
                  :disabled="!isStepAllowed(i)"
                  @click="goToStep(i)"
                ></button>
              </div>
            </div>
          </Transition>
          <Transition name="fade-right">
            <div v-show="!typing" :key="route.fullPath">
              <RouterView v-if="ready" />
            </div>
          </Transition>
        </div>
      </div>
    </PageContainer>
  </div>
</template>

<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core';
import { onMounted, ref, watch, provide, computed, onBeforeUnmount, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { CalendarProvider } from '@tallo/auth';
import { Button, Icon, PageContainer, Text, extractErrorMessage, useAlertsStore } from '@tallo/design-system';
import {
  InvestorOnboardingStep,
  PreferredCommunicationChannel,
  isOnboardingStepReachable,
  onboardingContinueActionKey,
  onboardingNavigationKey,
  onboardingTypingKey,
  resumeOnboarding,
  useInvestorStore,
  useOnboardingStore,
  visibleOnboardingSteps,
} from '@tallo/investor/investor';
import { AppRouteNames } from '@tallo/investor/navigation';
import { ScreeningSensitivity } from '@tallo/property';

const route = useRoute();
const router = useRouter();
const investorStore = useInvestorStore();
const alerts = useAlertsStore();

const store = useOnboardingStore();

const typedLines = ref<string[]>([]);
const typedText = ref('');
const typing = ref(false);
// Reduced motion renders each message at once instead of typing it out
const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
let typingRunId = 0;
// Set by pressing Space while the animation runs: the current message finishes instantly
// (remaining characters and lines render at once). Reset only when a new step's typing
// session starts (mount / route change), NOT inside the typing run itself — so a Space
// pressed during the initial waitFor() pause still skips the typing that follows.
let skipTypingRequested = false;

// Ref to track if continue button should be triggered on Enter
const continueAction = ref<(() => void) | null>(null);

// Both stores must be in before any step renders or any lock is judged: the steps seed their
// forms from them at setup, and the Finish lock reads the investor's completion flag
const ready = ref(false);
// A slow load must not navigate after the user has already left the wizard
let disposed = false;

provide(onboardingTypingKey, typing);

const isWelcome = computed(() => route.name === AppRouteNames.INVESTOR_ONBOARDING_WELCOME);

const stepRoutes: Record<InvestorOnboardingStep, AppRouteNames> = {
  [InvestorOnboardingStep.AccountType]: AppRouteNames.INVESTOR_ONBOARDING_ACCOUNT_TYPE,
  [InvestorOnboardingStep.CompanyInfo]: AppRouteNames.INVESTOR_ONBOARDING_COMPANY_INFO,
  [InvestorOnboardingStep.InviteTeam]: AppRouteNames.INVESTOR_ONBOARDING_INVITE_TEAM,
  [InvestorOnboardingStep.RentalStrategy]: AppRouteNames.INVESTOR_ONBOARDING_RENTAL_STRATEGY,
  [InvestorOnboardingStep.ConnectCalendar]: AppRouteNames.INVESTOR_ONBOARDING_CONNECT_CALENDAR,
  [InvestorOnboardingStep.Communication]: AppRouteNames.INVESTOR_ONBOARDING_COMMUNICATION,
};
const stepRouteNames: string[] = Object.values(stepRoutes);

// The dots: every step this account will see, plus the celebration screen
const stepperSteps = computed(() => [
  ...visibleOnboardingSteps(store.accountType, store.joinedExistingCompany).map((step) => ({
    step,
    name: stepRoutes[step],
  })),
  { step: null, name: AppRouteNames.INVESTOR_ONBOARDING_FINISH },
]);
const stepIndex = computed(() => stepperSteps.value.findIndex((s) => s.name === route.name));
const totalSteps = computed(() => stepperSteps.value.length);

function isStepAllowed(targetIndex: number) {
  const target = stepperSteps.value[targetIndex];
  if (!target) return false;
  // The celebration opens only once the server has marked the wizard complete, never on saved steps alone
  if (target.step === null) return Boolean(investorStore.investor?.onboarding?.initialOnboardingCompleted);
  return isOnboardingStepReachable(target.step, store.completedSteps, store.accountType, store.joinedExistingCompany);
}

function goToStep(index: number) {
  if (index < 0 || index >= stepperSteps.value.length) return;
  if (!isStepAllowed(index)) return;
  router.push({ name: stepperSteps.value[index].name });
}

function goBack() {
  if (stepIndex.value <= 0) router.push({ name: AppRouteNames.INVESTOR_ONBOARDING_WELCOME });
  else goToStep(stepIndex.value - 1);
}

// The step a returning user was dropped on; greeted there, cleared once they move on
const resumedAt = ref<AppRouteNames | null>(null);
// The step completing the wizard sent the user back to; told why there, cleared once they move on
const returnedTo = ref<AppRouteNames | null>(null);

function returnTo(step: InvestorOnboardingStep) {
  returnedTo.value = stepRoutes[step];
  router.push({ name: stepRoutes[step] });
}

function resumeRouteName(): AppRouteNames {
  const resume = resumeOnboarding(
    store.completedSteps,
    store.accountType,
    Boolean(investorStore.investor?.onboarding?.initialOnboardingCompleted),
    store.joinedExistingCompany,
  );
  if (resume.kind === 'welcome') return AppRouteNames.INVESTOR_ONBOARDING_WELCOME;
  if (resume.kind === 'finish') return AppRouteNames.INVESTOR_ONBOARDING_FINISH;
  return stepRoutes[resume.step];
}

// Keyboard event handler for Enter/Space keys
function handleKeydown(event: KeyboardEvent) {
  // Space skips the typing animation and shows the full message immediately
  if (event.key === ' ' && typing.value) {
    // Ignore Space typed into form controls so we don't hijack real input
    const target = event.target as HTMLElement | null;
    if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
    // Prevent page scroll and finish the typing animation instantly
    event.preventDefault();
    skipTypingRequested = true;
    return;
  }
  if (event.key === 'Enter' && continueAction.value && !typing.value) {
    // Prevent default form submission if we're handling it
    event.preventDefault();
    continueAction.value();
  }
}

// Guard against direct navigation to disallowed steps, and land the bare /onboarding parent
// route (the HOME guard and the logo link both send users there) on the right step
watch(
  () => [route.name, ready.value] as const,
  ([name, isReady]) => {
    if (!isReady) return;
    if (name === AppRouteNames.INVESTOR_ONBOARDING) {
      router.replace({ name: resumeRouteName() });
      return;
    }
    const idx = stepperSteps.value.findIndex((s) => s.name === name);
    if (idx < 0) {
      // A step this account never sees (the founder's invite step, for a seat that joined by
      // invite) has no dot to check; reached by its URL, it goes where the wizard would resume
      if (stepRouteNames.includes(name as string)) router.replace({ name: resumeRouteName() });
      return;
    }
    if (!isStepAllowed(idx)) {
      // find first allowed index
      let redirectIndex = 0;
      for (let i = 0; i < stepperSteps.value.length; i++) {
        if (!isStepAllowed(i)) break;
        redirectIndex = i;
      }
      router.replace({ name: stepperSteps.value[redirectIndex].name });
    }
  },
);

provide(onboardingNavigationKey, { goBack, returnTo });
provide(onboardingContinueActionKey, (action: (() => void) | null) => {
  continueAction.value = action;
});

onMounted(async () => {
  // Hide right panel immediately to avoid blink before typing kicks in
  typingRunId++;
  typing.value = true;
  document.addEventListener('keydown', handleKeydown);

  if (!store.loaded) {
    try {
      await store.load();
    } catch (error) {
      // Start over rather than dead-end: every step re-saves, so nothing is lost by re-answering
      alerts.error(extractErrorMessage(error, "We couldn't load your saved answers. Let's start from the top."));
      store.loaded = true;
    }
  }
  if (!investorStore.investor) {
    try {
      await investorStore.load();
    } catch (error) {
      console.error('Failed to load the investor for onboarding', error);
    }
  }
  if (disposed) return;
  ready.value = true;

  const target = resumeRouteName();
  if (target !== AppRouteNames.INVESTOR_ONBOARDING_WELCOME) resumedAt.value = target;
  if (route.name !== target) {
    // The route watcher below types for the new step
    router.replace({ name: target });
    return;
  }

  startTypingSession();
  typeForRoute(route.name as string);
});

onUnmounted(() => {
  // Remove keyboard event listener and reset store
  document.removeEventListener('keydown', handleKeydown);
  store.reset();
});

onBeforeUnmount(() => {
  disposed = true;
  typingRunId++;
});

watch(
  () => route.name,
  (name) => {
    if (resumedAt.value && name !== resumedAt.value) resumedAt.value = null;
    if (returnedTo.value && name !== returnedTo.value) returnedTo.value = null;
    startTypingSession();
    typeForRoute(name as string);
  },
);

// Hides the right panel and clears the previous message before a new one types
function startTypingSession() {
  typingRunId++; // invalidate previous typing run
  typing.value = true;
  skipTypingRequested = prefersReducedMotion.value;
  typedLines.value = [];
  typedText.value = '';
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function typeOutLines(lines: string[]) {
  const runId = ++typingRunId;
  typing.value = true;
  typedLines.value = [];
  typedText.value = '';
  for (const line of lines) {
    if (typingRunId !== runId) return; // cancelled
    await typeCharacters(line, runId);
    if (typingRunId !== runId) return; // cancelled
    typedLines.value.push(typedText.value);
    typedText.value = '';
    // No pause between lines when the user skipped the animation
    if (!skipTypingRequested) await sleep(105);
  }
  if (typingRunId !== runId) return;
  typing.value = false;
}

async function typeCharacters(line: string, runId: number) {
  typedText.value = '';
  for (let i = 0; i < line.length; i++) {
    if (typingRunId !== runId) return;
    // Skip requested (Space): render the whole line at once instead of char-by-char
    if (skipTypingRequested) {
      typedText.value = line;
      return;
    }
    typedText.value += line[i];
    await sleep(13 + Math.random() * 12);
  }
}

// The assistant makes the case for texts itself: its thread with the owner only works both ways over
// SMS, and the whole wizard speaks in its voice. Once a channel is saved the pitch is over
function communicationLines(channel: PreferredCommunicationChannel | null): string[] {
  if (channel === PreferredCommunicationChannel.Sms) {
    return ['Texts it is. I’m in your pocket — message me anytime.'];
  }
  if (channel === PreferredCommunicationChannel.Email) {
    return ['Email it is. Change your mind? Texts are right here.'];
  }
  return [
    'Last one — how should I reach you when something needs your call?',
    'My advice? Let’s text. I’ll be right there in your pocket.',
    'A heads-up from me when a renter needs you, a quick “book it” from you, and I take it from there. Text me anytime.',
  ];
}

async function typeForRoute(name: string) {
  const userName = investorStore.investor?.user?.name?.split(' ')[0] || 'there';
  const connectedCalendar = Boolean(
    investorStore.investor?.user?.calendars?.some((calendar) => calendar.provider === CalendarProvider.GOOGLE),
  );
  const channel = store.preferredCommunicationChannel;
  const joined = store.joinedExistingCompany;
  const team = store.companyName || 'your team';

  const copyMap: Record<string, string[]> = {
    [AppRouteNames.INVESTOR_ONBOARDING_WELCOME]: joined
      ? [
          `Welcome to ${team}, ${userName}! I’m Tallo, your team’s leasing assistant.`,
          'Your team has already set things up, so there are just two quick steps about you.',
        ]
      : [
          `Welcome, ${userName}! I’m Tallo, your leasing assistant.`,
          `Just a few quick steps and you’ll unlock scheduling, renter profiles, and faster decisions.`,
        ],
    [AppRouteNames.INVESTOR_ONBOARDING_ACCOUNT_TYPE]: [
      `First things first — are you an individual owner or a company?`,
      `I’ll tailor the setup around how you work.`,
    ],
    [AppRouteNames.INVESTOR_ONBOARDING_COMPANY_INFO]: [
      `Great — tell me a bit about your company, and I’ll set things up so everything runs smoothly.`,
    ],
    [AppRouteNames.INVESTOR_ONBOARDING_INVITE_TEAM]: [
      `Who else works on your properties? Invite them and they’ll get their own sign-in.`,
      `Just you for now? Skip this and invite people whenever you’re ready.`,
    ],
    [AppRouteNames.INVESTOR_ONBOARDING_RENTAL_STRATEGY]: [
      `Next, how strict should screening be before someone can book a showing?`,
      `You can go strict, keep it balanced, or review every single request yourself.`,
    ],
    [AppRouteNames.INVESTOR_ONBOARDING_CONNECT_CALENDAR]: connectedCalendar
      ? ['Nice — calendar connected! I’ll keep your availability in sync so scheduling stays smooth.']
      : joined
        ? [
            'First, your calendar: the showings of the properties you manage go on it.',
            'Want to connect it so I can schedule without the back-and-forth? You can skip this for now.',
          ]
        : [
            store.screeningSensitivity === ScreeningSensitivity.STRICT
              ? 'I’ll be selective so only qualified renters can book showings.'
              : store.screeningSensitivity === ScreeningSensitivity.MODERATE
                ? 'I’ll balance standards with momentum to keep showings moving.'
                : 'I’ll open the funnel wide and let you review showing requests manually.',
            'Want to connect your calendar so I can schedule without the back-and-forth? You can skip this for now.',
          ],
    [AppRouteNames.INVESTOR_ONBOARDING_COMMUNICATION]: [
      connectedCalendar
        ? 'Great — your calendar’s set.'
        : 'No problem. You can connect a calendar later in your profile.',
      ...communicationLines(channel),
    ],
    [AppRouteNames.INVESTOR_ONBOARDING_FINISH]: joined
      ? ['All set — nice work!', `${team}’s properties are waiting for you.`]
      : ['All set — nice work!', 'When you’re ready, add your first property to kick things off.'],
  };
  const lines = copyMap[name] || [];
  if (resumedAt.value === name) {
    lines.unshift(`Welcome back, ${userName} — I kept your answers.`);
  }
  if (returnedTo.value === name) {
    lines.unshift('Almost done — this step still needs an answer before we can finish.');
  }
  await typeOutLines(lines);
}

function start() {
  const [first] = visibleOnboardingSteps(store.accountType, store.joinedExistingCompany);

  router.push({ name: first ? stepRoutes[first] : AppRouteNames.INVESTOR_ONBOARDING_ACCOUNT_TYPE });
}

// Watch for welcome step to register start action
watch(
  isWelcome,
  (isWelcome) => {
    if (isWelcome && !typing.value) {
      continueAction.value = start;
    } else if (isWelcome) {
      continueAction.value = null;
    }
  },
  { immediate: true },
);

// Also watch typing state for welcome step
watch(typing, (typing) => {
  if (isWelcome.value) {
    continueAction.value = typing ? null : start;
  }
});
</script>

<style scoped lang="scss">
.onboarding {
  height: 100%;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr; /* 50/50 */
  align-items: start;
  gap: var(--t-spacing-xl);
  padding: var(--t-spacing-xl) var(--t-spacing-lg);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.left {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-xl);
}
.top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.assistant-row {
  display: flex;
  align-items: center;
  gap: var(--t-spacing-xs);
}
.assistant-icon {
  color: #fe6723;
}

.right .dot-row .step-dot.locked {
  background: transparent;
  box-shadow: none;
  border: 1px dashed var(--t-color-neutral-300);
}

.typed {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-md);
  margin-top: 0;
  min-height: 6rem;
}

.right {
  min-height: 50vh;
}
.right-step-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: var(--t-spacing-lg);
}
.dot-row {
  display: inline-flex;
  gap: 8px;
}
/* Stepper: light orange for done, solid for active, gray for future; state changes ease rather than snap */
.step-dot {
  box-sizing: border-box;
  flex: none;
  width: 8px;
  height: 8px;
  padding: 0;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: var(--t-color-neutral-300);
  transition:
    background-color 150ms ease,
    box-shadow 150ms ease;
}
.step-dot.done {
  background: rgba(254, 103, 35, 0.35);
}
.step-dot.active {
  background: #fe6723;
  box-shadow: 0 0 0 4px rgba(254, 103, 35, 0.14);
}

/* The right panel slides in from the right: a forward-moving wizard, so new content arrives from ahead */
.fade-right-enter-active {
  transition:
    opacity 220ms var(--t-ease-out),
    transform 220ms var(--t-ease-out);
}
.fade-right-leave-active {
  transition:
    opacity 150ms var(--t-ease-out),
    transform 150ms var(--t-ease-out);
}
.fade-right-enter-from {
  opacity: 0;
  transform: translateX(16px);
}
.fade-right-leave-to {
  opacity: 0;
  transform: translateX(8px);
}

/* The welcome button fades in once the greeting is typed; it never needs to animate out */
.btn-fade-enter-active {
  transition: opacity 160ms var(--t-ease-out);
}
.btn-fade-leave-active {
  transition: none;
}
.btn-fade-enter-from,
.btn-fade-leave-to {
  opacity: 0;
}
.welcome-cta {
  margin-top: var(--t-spacing-md);
}

/* Keep the fades, drop the movement */
@media (prefers-reduced-motion: reduce) {
  .fade-right-enter-from,
  .fade-right-leave-to {
    transform: none;
  }
}

/* Reserve enough space for typing on small screens so stepper/form don't jump */
@media (max-width: 768px) {
  .typed {
    min-height: 10rem;
  }
}
@media (max-width: 520px) {
  .typed {
    min-height: 12.5rem;
  }
}

.typed {
  padding-left: var(--t-spacing-sm);
  padding-right: var(--t-spacing-sm);
}
.right {
  padding-left: var(--t-spacing-sm);
  padding-right: var(--t-spacing-sm);
}

@media (max-width: 1024px) {
  .grid {
    grid-template-columns: 1fr;
    padding-left: var(--t-spacing-md);
    padding-right: var(--t-spacing-md);
  }
}
</style>
